import { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, MouseSensor, TouchSensor, useSensor, useSensors, useDraggable, useDroppable, closestCenter, pointerWithin, rectIntersection } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Eye, Settings, Copy, Trash2, Play, Pause, BarChart3, Globe, Smartphone, Tablet, Monitor, Edit2, Move, X } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import UpdatesEditor from "@/components/updates-editor";
import type { LandingPage, LandingPageVersion, LandingPageElement, LandingPageExperiment } from "@shared/schema";

// Element Editor Components
function ElementTextEditor({ element, onSave, onCancel }: { element: LandingPageElement; onSave: (props: any) => void; onCancel: () => void }) {
  const [props, setProps] = useState((element.props as any) || {});

  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="text-content" className="text-lg font-semibold mb-3 block">Rich Text Content</Label>
        <div className="border rounded-lg overflow-hidden" data-testid="rich-text-editor">
          <UpdatesEditor
            content={props.content || ""}
            onUpdate={(content) => setProps((prev: any) => ({ ...prev, content }))}
            placeholder="Enter your text content with rich formatting..."
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="text-alignment">Text Alignment</Label>
          <Select value={props.textAlign || "left"} onValueChange={(value) => setProps((prev: any) => ({ ...prev, textAlign: value }))}>
            <SelectTrigger data-testid="select-text-align">
              <SelectValue placeholder="Select alignment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">Left</SelectItem>
              <SelectItem value="center">Center</SelectItem>
              <SelectItem value="right">Right</SelectItem>
              <SelectItem value="justify">Justify</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="font-weight">Font Weight</Label>
          <Select value={props.fontWeight || "normal"} onValueChange={(value) => setProps((prev: any) => ({ ...prev, fontWeight: value }))}>
            <SelectTrigger data-testid="select-font-weight">
              <SelectValue placeholder="Select weight" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="bold">Bold</SelectItem>
              <SelectItem value="lighter">Light</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="flex gap-3 pt-4">
        <Button onClick={() => onSave(props)} data-testid="button-save-text" className="flex-1">
          Save Changes
        </Button>
        <Button variant="outline" onClick={onCancel} data-testid="button-cancel-text" className="flex-1">
          Cancel
        </Button>
      </div>
    </div>
  );
}

function ElementButtonEditor({ element, onSave, onCancel }: { element: LandingPageElement; onSave: (props: any) => void; onCancel: () => void }) {
  const [props, setProps] = useState((element.props as any) || {});

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="button-text">Button Text</Label>
        <Input
          id="button-text"
          value={props.text || ""}
          onChange={(e) => setProps((prev: any) => ({ ...prev, text: e.target.value }))}
          placeholder="Click me"
          data-testid="input-button-text"
        />
      </div>
      <div>
        <Label htmlFor="button-color">Background Color</Label>
        <Input
          id="button-color"
          type="color"
          value={props.backgroundColor || "#007bff"}
          onChange={(e) => setProps((prev: any) => ({ ...prev, backgroundColor: e.target.value }))}
          data-testid="input-button-color"
        />
      </div>
      <div>
        <Label htmlFor="button-href">Link URL</Label>
        <Input
          id="button-href"
          value={props.href || ""}
          onChange={(e) => setProps((prev: any) => ({ ...prev, href: e.target.value }))}
          placeholder="https://example.com"
          data-testid="input-button-href"
        />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => onSave(props)} data-testid="button-save-button">Save Changes</Button>
        <Button variant="outline" onClick={onCancel} data-testid="button-cancel-button">Cancel</Button>
      </div>
    </div>
  );
}

function ElementImageEditor({ element, onSave, onCancel }: { element: LandingPageElement; onSave: (props: any) => void; onCancel: () => void }) {
  const [props, setProps] = useState((element.props as any) || {});

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="image-src">Image URL</Label>
        <Input
          id="image-src"
          value={props.src || ""}
          onChange={(e) => setProps((prev: any) => ({ ...prev, src: e.target.value }))}
          placeholder="https://example.com/image.jpg"
          data-testid="input-image-src"
        />
      </div>
      <div>
        <Label htmlFor="image-alt">Alt Text</Label>
        <Input
          id="image-alt"
          value={props.alt || ""}
          onChange={(e) => setProps((prev: any) => ({ ...prev, alt: e.target.value }))}
          placeholder="Image description"
          data-testid="input-image-alt"
        />
      </div>
      <div>
        <Label htmlFor="image-width">Width</Label>
        <Input
          id="image-width"
          value={props.width || "400px"}
          onChange={(e) => setProps((prev: any) => ({ ...prev, width: e.target.value }))}
          placeholder="400px"
          data-testid="input-image-width"
        />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => onSave(props)} data-testid="button-save-image">Save Changes</Button>
        <Button variant="outline" onClick={onCancel} data-testid="button-cancel-image">Cancel</Button>
      </div>
    </div>
  );
}

function ElementPricingEditor({ element, onSave, onCancel }: { element: LandingPageElement; onSave: (props: any) => void; onCancel: () => void }) {
  const [props, setProps] = useState((element.props as any) || { plans: [{ name: "Basic", price: "$9/month", features: ["Feature 1"], featured: false }] });

  const addPlan = () => {
    setProps((prev: any) => ({
      ...prev,
      plans: [...(prev.plans || []), { name: "New Plan", price: "$0/month", features: ["New Feature"], featured: false }]
    }));
  };

  const updatePlan = (index: number, field: string, value: any) => {
    setProps((prev: any) => ({
      ...prev,
      plans: prev.plans.map((plan: any, i: number) => 
        i === index ? { ...plan, [field]: value } : plan
      )
    }));
  };

  const removePlan = (index: number) => {
    setProps((prev: any) => ({
      ...prev,
      plans: prev.plans.filter((_: any, i: number) => i !== index)
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Label>Pricing Plans</Label>
        <Button onClick={addPlan} size="sm" data-testid="button-add-plan">Add Plan</Button>
      </div>
      {props.plans?.map((plan: any, index: number) => (
        <div key={index} className="border p-4 rounded-lg space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="font-medium">Plan {index + 1}</h4>
            <Button onClick={() => removePlan(index)} size="sm" variant="outline" data-testid={`button-remove-plan-${index}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
          <div>
            <Label htmlFor={`plan-name-${index}`}>Plan Name</Label>
            <Input
              id={`plan-name-${index}`}
              value={plan.name || ""}
              onChange={(e) => updatePlan(index, "name", e.target.value)}
              placeholder="Basic Plan"
              data-testid={`input-plan-name-${index}`}
            />
          </div>
          <div>
            <Label htmlFor={`plan-price-${index}`}>Price</Label>
            <Input
              id={`plan-price-${index}`}
              value={plan.price || ""}
              onChange={(e) => updatePlan(index, "price", e.target.value)}
              placeholder="$9/month"
              data-testid={`input-plan-price-${index}`}
            />
          </div>
          <div>
            <Label htmlFor={`plan-features-${index}`}>Features (one per line)</Label>
            <Textarea
              id={`plan-features-${index}`}
              value={(plan.features || []).join('\n')}
              onChange={(e) => updatePlan(index, "features", e.target.value.split('\n').filter(f => f.trim()))}
              placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
              data-testid={`textarea-plan-features-${index}`}
            />
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <Button onClick={() => onSave(props)} data-testid="button-save-pricing">Save Changes</Button>
        <Button variant="outline" onClick={onCancel} data-testid="button-cancel-pricing">Cancel</Button>
      </div>
    </div>
  );
}

// Element Library Components
const elementTypes = [
  { type: "text", label: "Text Block", icon: "T", description: "Add headings and paragraphs" },
  { type: "image", label: "Image", icon: "🖼️", description: "Upload or embed images" },
  { type: "button", label: "Button", icon: "🔘", description: "Call-to-action buttons" },
  { type: "form", label: "Form", icon: "📝", description: "Contact or signup forms" },
  { type: "video", label: "Video", icon: "🎥", description: "Embed videos" },
  { type: "testimonial", label: "Testimonial", icon: "💬", description: "Customer quotes" },
  { type: "feature", label: "Feature", icon: "⭐", description: "Feature highlights" },
  { type: "pricing", label: "Pricing", icon: "💰", description: "Pricing tables" },
];

interface DraggableElementProps {
  elementType: typeof elementTypes[0];
  onDragStart?: () => void;
}

function DraggableElement({ elementType, onDragStart }: DraggableElementProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `element-${elementType.type}`,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`p-3 border rounded-lg cursor-grab transition-all ${
        isDragging 
          ? 'opacity-50 border-blue-500 bg-blue-50' 
          : 'border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
      }`}
      data-testid={`element-${elementType.type}`}
      style={{ touchAction: 'none', userSelect: 'none' }}
    >
      <div className="flex items-center gap-2">
        <span className="text-lg">{elementType.icon}</span>
        <div>
          <span className="font-medium text-sm text-gray-800 dark:text-gray-200">{elementType.label}</span>
          <p className="text-xs text-gray-500 dark:text-gray-400">{elementType.description}</p>
        </div>
      </div>
    </div>
  );
}

// Clean Droppable Canvas Component - No visual clutter
function DroppableCanvas({ children, isOver }: { children: React.ReactNode; isOver: boolean }) {
  const { setNodeRef } = useDroppable({
    id: 'canvas-drop-zone',
  });

  return (
    <div 
      ref={setNodeRef}
      className={`w-full border-2 rounded-lg min-h-[600px] transition-all relative ${
        isOver 
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/10' 
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
      }`}
      data-testid="canvas-area"
    >
      {/* Subtle drop indicator */}
      {isOver && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg">
            Drop element here
          </div>
        </div>
      )}
      
      {children}
    </div>
  );
}

// Add Element Toolbar - Click to add widgets
function AddElementToolbar({ onAddElement, disabled }: { onAddElement: (type: string) => void; disabled: boolean }) {
  return (
    <div className="flex flex-wrap gap-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-full mb-2">Click to add:</span>
      {elementTypes.map((elementType) => (
        <Button
          key={elementType.type}
          variant="outline"
          size="sm"
          onClick={() => onAddElement(elementType.type)}
          disabled={disabled}
          className="flex items-center gap-2 hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-blue-900/20"
          data-testid={`add-${elementType.type}-btn`}
        >
          <span>{elementType.icon}</span>
          <span>{elementType.label}</span>
        </Button>
      ))}
    </div>
  );
}

export default function LandingPageBuilder() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isOverCanvas, setIsOverCanvas] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<"desktop" | "tablet" | "mobile">("desktop");
  const [showCreatePageDialog, setShowCreatePageDialog] = useState(false);
  const [showCreateVersionDialog, setShowCreateVersionDialog] = useState(false);
  const [newPageData, setNewPageData] = useState({ name: "", slug: "", description: "" });
  const [newVersionData, setNewVersionData] = useState({ name: "", description: "" });
  const [editingElement, setEditingElement] = useState<LandingPageElement | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCanvasSettings, setShowCanvasSettings] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 2, // Reduced distance for easier activation
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100, // Reduced delay
        tolerance: 10,
      },
    })
  );

  // Fetch landing pages
  const { data: landingPages = [], isLoading: isLoadingPages } = useQuery<LandingPage[]>({
    queryKey: ["/api/landing-pages"],
  });

  // Fetch versions for selected page
  const { data: versions = [], isLoading: isLoadingVersions } = useQuery<LandingPageVersion[]>({
    queryKey: ["/api/landing-pages", selectedPageId, "versions"],
    enabled: !!selectedPageId,
  });

  // Fetch elements for selected version
  const { data: elements = [], isLoading: isLoadingElements } = useQuery<LandingPageElement[]>({
    queryKey: ["/api/landing-pages/versions", selectedVersionId, "elements"],
    enabled: !!selectedVersionId,
  });

  // Fetch experiments for selected page
  const { data: experiments = [], isLoading: isLoadingExperiments } = useQuery<LandingPageExperiment[]>({
    queryKey: ["/api/landing-pages", selectedPageId, "experiments"],
    enabled: !!selectedPageId,
  });

  // Create page mutation
  const createPageMutation = useMutation({
    mutationFn: async (data: typeof newPageData) => {
      const response = await apiRequest("POST", "/api/landing-pages", data);
      return await response.json();
    },
    onSuccess: async (newPage: LandingPage) => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages"] });
      setSelectedPageId(newPage.id);
      
      // Create default version for the new page
      try {
        const defaultVersionData = {
          name: "Version A",
          description: "Default version",
          variantKey: `variant-${Date.now()}`,
          structure: { sections: [] },
          trafficSplit: "100",
          status: "draft",
        };
        const versionResponse = await apiRequest("POST", `/api/landing-pages/${newPage.id}/versions`, defaultVersionData);
        const newVersion = await versionResponse.json();
        
        // Auto-select the new version to show canvas immediately
        setSelectedVersionId(newVersion.id);
        queryClient.invalidateQueries({ queryKey: ["/api/landing-pages", newPage.id, "versions"] });
        
      } catch (error) {
        console.error("Failed to create default version:", error);
      }
      
      setShowCreatePageDialog(false);
      setNewPageData({ name: "", slug: "", description: "" });
    },
    onError: () => {
    },
  });

  // Create version mutation
  const createVersionMutation = useMutation({
    mutationFn: async (data: typeof newVersionData) => {
      const versionData = {
        ...data,
        variantKey: `variant-${Date.now()}`,
        structure: { sections: [] },
        trafficSplit: "0",
        status: "draft",
      };
      const response = await apiRequest("POST", `/api/landing-pages/${selectedPageId}/versions`, versionData);
      return await response.json();
    },
    onSuccess: (newVersion: LandingPageVersion) => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages", selectedPageId, "versions"] });
      setSelectedVersionId(newVersion.id);
      setShowCreateVersionDialog(false);
      setNewVersionData({ name: "", description: "" });
    },
    onError: () => {
    },
  });

  // Create element mutation
  const createElementMutation = useMutation({
    mutationFn: async (data: { type: string; props: any; order: number; versionId: string }) => {
      console.log("Creating element with versionId:", data.versionId);
      const elementData = {
        type: data.type,
        props: data.props,
        order: data.order,
        parentId: null,
      };
      const response = await apiRequest("POST", `/api/landing-pages/versions/${data.versionId}/elements`, elementData);
      return await response.json();
    },
    onSuccess: (newElement, variables) => {
      console.log("Element created successfully:", newElement);
      console.log("Invalidating cache for versionId:", variables.versionId);
      
      // Invalidate multiple cache patterns to ensure update
      queryClient.invalidateQueries({ 
        queryKey: ["/api/landing-pages/versions", variables.versionId, "elements"] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ["/api/landing-pages/versions"] 
      });
      
      // Force immediate refetch
      queryClient.refetchQueries({ 
        queryKey: ["/api/landing-pages/versions", variables.versionId, "elements"] 
      });
      
    },
    onError: (error) => {
      console.error("Failed to create element:", error);
    },
  });

  // Update element mutation
  const updateElementMutation = useMutation({
    mutationFn: async (data: { id: string; props: any; order?: number }) => {
      const response = await apiRequest("PUT", `/api/landing-pages/elements/${data.id}`, {
        props: data.props,
        order: data.order,
      });
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ["/api/landing-pages/versions", selectedVersionId, "elements"] 
      });
      setShowEditDialog(false);
      setEditingElement(null);
    },
    onError: (error) => {
      console.error("Failed to update element:", error);
    },
  });

  // Delete element mutation
  const deleteElementMutation = useMutation({
    mutationFn: async (elementId: string) => {
      const response = await apiRequest("DELETE", `/api/landing-pages/elements/${elementId}`);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ["/api/landing-pages/versions", selectedVersionId, "elements"] 
      });
    },
    onError: (error) => {
      console.error("Failed to delete element:", error);
    },
  });

  // Delete landing page mutation
  const deleteLandingPageMutation = useMutation({
    mutationFn: async (pageId: string) => {
      const response = await apiRequest("DELETE", `/api/landing-pages/${pageId}`);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages"] });
      // Clear selected page if it was deleted
      setSelectedPageId(null);
      setSelectedVersionId(null);
    },
    onError: (error) => {
      console.error("Failed to delete landing page:", error);
    },
  });

  // Seed native landing page mutation
  const seedNativePageMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/landing-pages/seed-native");
      if (response.status === 409) {
        const data = await response.json();
        throw new Error(data.message || "Native Landing Page already exists");
      }
      return await response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages"] });
      if (data.page) {
        setSelectedPageId(data.page.id);
      }
      if (data.version) {
        setSelectedVersionId(data.version.id);
      }
      toast({
        title: "Native Landing Page Created",
        description: `Created page with ${data.elementsCount} editable elements`,
      });
    },
    onError: (error: any) => {
      console.error("Failed to seed native page:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create native landing page",
        variant: "destructive",
      });
    },
  });

  // Handle element edit
  function handleEditElement(element: LandingPageElement) {
    setEditingElement(element);
    setShowEditDialog(true);
  }

  // Handle element delete
  function handleDeleteElement(elementId: string) {
    if (confirm("Are you sure you want to delete this element?")) {
      deleteElementMutation.mutate(elementId);
    }
  }

  // Handle landing page delete
  function handleDeleteLandingPage(pageId: string, pageName: string) {
    if (confirm(`Are you sure you want to delete the landing page "${pageName}"? This action cannot be undone.`)) {
      deleteLandingPageMutation.mutate(pageId);
    }
  }

  // Handle save element changes
  function handleSaveElement(updatedProps: any) {
    if (editingElement) {
      updateElementMutation.mutate({
        id: editingElement.id,
        props: updatedProps,
      });
    }
  }

  const handleResizeStart = (e: React.MouseEvent, element: LandingPageElement, direction: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = parseInt((element.props as any)?.width) || 200;
    const startHeight = parseInt((element.props as any)?.height) || 100;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      
      let newWidth = startWidth;
      let newHeight = startHeight;
      
      // Calculate new dimensions based on resize direction
      switch (direction) {
        case 'se': // Bottom-right
          newWidth = Math.max(50, startWidth + deltaX);
          newHeight = Math.max(30, startHeight + deltaY);
          break;
        case 'sw': // Bottom-left
          newWidth = Math.max(50, startWidth - deltaX);
          newHeight = Math.max(30, startHeight + deltaY);
          break;
        case 'ne': // Top-right
          newWidth = Math.max(50, startWidth + deltaX);
          newHeight = Math.max(30, startHeight - deltaY);
          break;
        case 'nw': // Top-left
          newWidth = Math.max(50, startWidth - deltaX);
          newHeight = Math.max(30, startHeight - deltaY);
          break;
      }
      
      // Update element dimensions immediately for visual feedback
      const elementDiv = document.querySelector(`[data-testid="element-${element.id}"]`) as HTMLElement;
      if (elementDiv) {
        elementDiv.style.width = `${newWidth}px`;
        elementDiv.style.height = `${newHeight}px`;
      }
    };
    
    const handleMouseUp = async () => {
      const elementDiv = document.querySelector(`[data-testid="element-${element.id}"]`) as HTMLElement;
      if (elementDiv) {
        const finalWidth = parseInt(elementDiv.style.width);
        const finalHeight = parseInt(elementDiv.style.height);
        
        // Save to database
        try {
          await updateElementMutation.mutateAsync({
            id: element.id,
            props: {
              ...(element.props as any),
              width: finalWidth.toString(),
              height: finalHeight.toString()
            }
          });
        } catch (error) {
          console.error("Failed to save resize:", error);
        }
      }
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Handle click-to-add element from toolbar
  function handleAddElementClick(type: string) {
    if (!selectedVersionId) return;
    
    const defaultProps = getDefaultPropsForType(type);
    const newOrder = elements.length;
    
    createElementMutation.mutate({
      type,
      props: defaultProps,
      order: newOrder,
      versionId: selectedVersionId,
    });
  }

  // Get default props for element type
  function getDefaultPropsForType(type: string): any {
    switch (type) {
      case "text":
        return { content: "<p>Enter your text here...</p>", textAlign: "left", fontWeight: "normal" };
      case "button":
        return { text: "Click me", backgroundColor: "#3b82f6", color: "#ffffff", href: "" };
      case "image":
        return { src: "https://via.placeholder.com/400x200", alt: "Image description", width: "400px" };
      case "form":
        return { title: "Contact Us", fields: ["name", "email", "message"], buttonText: "Submit" };
      case "video":
        return { url: "", title: "Video Title" };
      case "testimonial":
        return { quote: "This is an amazing product!", author: "John Doe", role: "CEO" };
      case "feature":
        return { title: "Feature Title", description: "Feature description goes here", icon: "⭐" };
      case "pricing":
        return { plans: [{ name: "Basic", price: "$9/month", features: ["Feature 1", "Feature 2"], featured: false }] };
      default:
        return {};
    }
  }

  // Handle drag start
  function handleDragStart(event: DragStartEvent) {
    console.log("🟢 Drag started:", event.active.id);
    setActiveId(event.active.id as string);
  }

  function handleDragOver(event: any) {
    const { over } = event;
    const isOverCanvasNow = over?.id === 'canvas-drop-zone';
    console.log("🔄 Drag over:", over?.id, "isOverCanvas:", isOverCanvasNow);
    setIsOverCanvas(isOverCanvasNow);
  }

  // Handle drag end
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    console.log("🔴 Drag ended:", active.id, "over:", over?.id);
    
    setActiveId(null);
    setIsOverCanvas(false);

    if (!over) {
      console.log("❌ No drop target detected");
      return;
    }

    if (over.id !== 'canvas-drop-zone') {
      console.log("❌ Not dropped on canvas, dropped on:", over.id);
      return;
    }
    
    if (!selectedVersionId) {
      console.log("❌ No version selected");
      return;
    }

    // If dropping a new element from the library
    if (active.id.toString().startsWith("element-")) {
      const elementType = active.id.toString().replace("element-", "");
      const defaultProps = getDefaultPropsForElementType(elementType);
      
      console.log("✅ Creating element:", elementType, "for version:", selectedVersionId);
      
      createElementMutation.mutate({
        type: elementType,
        props: defaultProps,
        order: elements?.length || 0,
        versionId: selectedVersionId,
      });
    }
  }

  // Get default props for element type
  function getDefaultPropsForElementType(type: string) {
    const defaults: Record<string, any> = {
      text: { 
        content: "New text block", 
        fontSize: "16px", 
        color: "#000000",
        fontWeight: "normal",
        textAlign: "left"
      },
      image: { 
        src: "https://via.placeholder.com/400x300", 
        alt: "Placeholder image",
        width: "400px",
        height: "300px"
      },
      button: { 
        text: "Click me", 
        href: "#",
        backgroundColor: "#007bff",
        color: "#ffffff",
        borderRadius: "4px",
        padding: "12px 24px"
      },
      form: { 
        fields: [
          { type: "email", label: "Email", required: true },
          { type: "submit", text: "Submit" }
        ]
      },
      video: { 
        src: "", 
        autoplay: false,
        controls: true,
        width: "100%",
        height: "315px"
      },
      testimonial: { 
        quote: "This product changed my life!",
        author: "John Doe",
        company: "Example Corp",
        avatar: "https://via.placeholder.com/64x64"
      },
      feature: { 
        icon: "⭐",
        title: "Amazing Feature",
        description: "This feature will help you achieve your goals."
      },
      pricing: { 
        plans: [
          {
            name: "Basic",
            price: "$9/month",
            features: ["Feature 1", "Feature 2"],
            featured: false
          }
        ]
      },
    };
    return defaults[type] || {};
  }

  // Auto-create and select page and version if none exist
  useEffect(() => {
    if (!isLoadingPages && landingPages.length === 0) {
      // Auto-create a default page if none exist
      const defaultPageData = {
        name: "My Landing Page",
        slug: "my-landing-page",
        description: "Auto-created default landing page"
      };
      createPageMutation.mutate(defaultPageData);
    } else if (landingPages.length > 0 && !selectedPageId) {
      setSelectedPageId(landingPages[0].id);
    }
  }, [landingPages, selectedPageId, isLoadingPages]);

  useEffect(() => {
    if (versions.length > 0 && !selectedVersionId) {
      setSelectedVersionId(versions[0].id);
    }
  }, [versions, selectedVersionId]);

  if (isLoadingPages) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-1 space-y-4">
                <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
              <div className="lg:col-span-3">
                <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900" data-testid="landing-page-builder">
      <DndContext 
        sensors={sensors} 
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart} 
        onDragOver={handleDragOver} 
        onDragEnd={handleDragEnd}
      >
        <div className="w-full px-4 sm:px-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-testid="page-title">
                Landing Page Builder
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                Click buttons to add elements or drag from the sidebar
              </p>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => seedNativePageMutation.mutate()}
                disabled={seedNativePageMutation.isPending}
                data-testid="button-seed-native-page"
              >
                <Globe className="w-4 h-4 mr-2" />
                {seedNativePageMutation.isPending ? "Creating..." : "Create Native Page"}
              </Button>
              <Dialog open={showCreatePageDialog} onOpenChange={setShowCreatePageDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="button-create-page">
                    <Plus className="w-4 h-4 mr-2" />
                    New Page
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="dialog-create-page" className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Landing Page</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="page-name">Page Name</Label>
                      <Input
                        id="page-name"
                        value={newPageData.name}
                        onChange={(e) => setNewPageData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="My Landing Page"
                        data-testid="input-page-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="page-slug">URL Slug</Label>
                      <Input
                        id="page-slug"
                        value={newPageData.slug}
                        onChange={(e) => setNewPageData(prev => ({ ...prev, slug: e.target.value }))}
                        placeholder="my-landing-page"
                        data-testid="input-page-slug"
                      />
                    </div>
                    <div>
                      <Label htmlFor="page-description">Description (Optional)</Label>
                      <Textarea
                        id="page-description"
                        value={newPageData.description}
                        onChange={(e) => setNewPageData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Description of your landing page"
                        data-testid="textarea-page-description"
                      />
                    </div>
                    <Button 
                      onClick={() => createPageMutation.mutate(newPageData)}
                      disabled={createPageMutation.isPending || !newPageData.name || !newPageData.slug}
                      className="w-full"
                      data-testid="button-submit-page"
                    >
                      {createPageMutation.isPending ? "Creating..." : "Create Page"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Left Sidebar - Collapsible Page & Element Library */}
            <div className="lg:col-span-1 space-y-4">
              {/* Page Selection */}
              <Card data-testid="card-page-selection">
                <CardHeader>
                  <CardTitle className="text-lg">Pages</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {landingPages.map((page: LandingPage) => (
                    <div
                      key={page.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedPageId === page.id
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                      }`}
                      data-testid={`page-item-${page.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0" onClick={() => setSelectedPageId(page.id)}>
                          <h3 className="font-medium text-sm">{page.name}</h3>
                          <p className="text-xs text-gray-600 dark:text-gray-400">/{page.slug}</p>
                          {page.description && (
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 line-clamp-2">
                              {page.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={page.isActive ? "default" : "secondary"}>
                            {page.isActive ? "Active" : "Draft"}
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteLandingPage(page.id, page.name);
                            }}
                            disabled={deleteLandingPageMutation.isPending}
                            className="h-6 w-6 p-0 hover:bg-red-50 hover:border-red-300 dark:hover:bg-red-900/20"
                            data-testid={`button-delete-page-${page.id}`}
                          >
                            <Trash2 className="w-3 h-3 text-red-600 dark:text-red-400" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Element Library */}
              <Card data-testid="card-element-library">
                <CardHeader>
                  <CardTitle className="text-lg">Element Library</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
                    {elementTypes.map((elementType) => (
                      <div key={elementType.type} data-element-type={elementType.type}>
                        <DraggableElement 
                          elementType={elementType}
                          onDragStart={() => setActiveId(`element-${elementType.type}`)}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content Area - Canvas and Controls */}
            <div className="lg:col-span-4 space-y-4">
              {selectedPageId && (
                <>
                  {/* Version Management */}
                  <Card data-testid="card-version-management">
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-lg">Versions & A/B Testing</CardTitle>
                        <div className="flex gap-2">
                          <Select value={previewDevice} onValueChange={(value: any) => setPreviewDevice(value)}>
                            <SelectTrigger className="w-32" data-testid="select-preview-device">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="desktop" data-testid="device-desktop">
                                <div className="flex items-center gap-2">
                                  <Monitor className="w-4 h-4" />
                                  Desktop
                                </div>
                              </SelectItem>
                              <SelectItem value="tablet" data-testid="device-tablet">
                                <div className="flex items-center gap-2">
                                  <Tablet className="w-4 h-4" />
                                  Tablet
                                </div>
                              </SelectItem>
                              <SelectItem value="mobile" data-testid="device-mobile">
                                <div className="flex items-center gap-2">
                                  <Smartphone className="w-4 h-4" />
                                  Mobile
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <Dialog open={showCreateVersionDialog} onOpenChange={setShowCreateVersionDialog}>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" data-testid="button-create-version">
                                <Plus className="w-4 h-4 mr-1" />
                                New Version
                              </Button>
                            </DialogTrigger>
                            <DialogContent data-testid="dialog-create-version" className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Create New Version</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <Label htmlFor="version-name">Version Name</Label>
                                  <Input
                                    id="version-name"
                                    value={newVersionData.name}
                                    onChange={(e) => setNewVersionData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Version B"
                                    data-testid="input-version-name"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="version-description">Description (Optional)</Label>
                                  <Textarea
                                    id="version-description"
                                    value={newVersionData.description}
                                    onChange={(e) => setNewVersionData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Description of this version"
                                    data-testid="textarea-version-description"
                                  />
                                </div>
                                <Button 
                                  onClick={() => createVersionMutation.mutate(newVersionData)}
                                  disabled={createVersionMutation.isPending || !newVersionData.name}
                                  className="w-full"
                                  data-testid="button-submit-version"
                                >
                                  {createVersionMutation.isPending ? "Creating..." : "Create Version"}
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {versions.map((version: LandingPageVersion) => (
                          <div
                            key={version.id}
                            className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                              selectedVersionId === version.id
                                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                            }`}
                            onClick={() => setSelectedVersionId(version.id)}
                            data-testid={`version-item-${version.id}`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-medium text-sm">{version.name}</h3>
                              <Badge variant={version.status === "published" ? "default" : "secondary"}>
                                {version.status}
                              </Badge>
                            </div>
                            {version.description && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                {version.description}
                              </p>
                            )}
                            <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-500">
                              <span>Traffic: {version.trafficSplit}%</span>
                              <span>{version.variantKey}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Canvas Area - Full width with toolbar */}
                  {selectedVersionId && (
                    <Card data-testid="card-canvas">
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-lg">Canvas</CardTitle>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => setShowPreview(true)}
                              data-testid="button-preview"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => setShowCanvasSettings(true)}
                              data-testid="button-settings"
                            >
                              <Settings className="w-4 h-4 mr-1" />
                              Settings
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Click-to-Add Toolbar */}
                        <AddElementToolbar 
                          onAddElement={handleAddElementClick} 
                          disabled={!selectedVersionId || createElementMutation.isPending}
                        />
                        
                        {/* Canvas with responsive width */}
                        <div className={
                          previewDevice === "mobile" ? "max-w-sm mx-auto" : 
                          previewDevice === "tablet" ? "max-w-2xl mx-auto" : 
                          "w-full"
                        }>
                          <DroppableCanvas isOver={isOverCanvas}>
                            <div className="p-6">
                              {isLoadingElements ? (
                                <div className="animate-pulse space-y-4">
                                  {[1, 2, 3].map((i) => (
                                    <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                  ))}
                                </div>
                              ) : elements.length === 0 ? (
                                <div className="text-center py-16 text-gray-500 dark:text-gray-400">
                                  <div className="mb-4">
                                    <Globe className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600" />
                                  </div>
                                  <h3 className="text-lg font-medium mb-2">No elements yet</h3>
                                  <p className="text-sm mb-4">Click any element above to add it, or drag from the library</p>
                                </div>
                              ) : (
                                <SortableContext items={elements.map((el: LandingPageElement) => el.id)} strategy={verticalListSortingStrategy}>
                                  <div className="space-y-3" data-testid="elements-container">
                                    {elements.map((element: LandingPageElement) => (
                                      <div
                                        key={element.id}
                                        className="group relative w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg transition-all hover:border-blue-400 hover:shadow-md"
                                        data-testid={`element-${element.id}`}
                                      >
                                        {/* Compact header with actions */}
                                        <div className="flex justify-between items-center px-4 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-t-lg">
                                          <div className="flex items-center gap-2">
                                            <span className="text-lg">{elementTypes.find(et => et.type === element.type)?.icon}</span>
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                              {elementTypes.find(et => et.type === element.type)?.label || element.type}
                                            </span>
                                          </div>
                                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleEditElement(element)}
                                              data-testid={`button-edit-${element.id}`}
                                              className="h-7 w-7 p-0"
                                            >
                                              <Edit2 className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleDeleteElement(element.id)}
                                              data-testid={`button-delete-${element.id}`}
                                              className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600"
                                            >
                                              <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                          </div>
                                        </div>
                                        
                                        {/* Element Content Preview */}
                                        <div className="p-4">
                                          {element.type === "text" && (
                                            <div 
                                              className="prose prose-sm max-w-none dark:prose-invert" 
                                              style={{ textAlign: (element.props as any)?.textAlign || "left" }}
                                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize((element.props as any)?.content || "<p>Text content</p>") }}
                                            />
                                          )}
                                          {element.type === "button" && (
                                            <div className="flex justify-center">
                                              <button className="px-6 py-3 rounded-lg font-medium" style={{
                                                backgroundColor: (element.props as any)?.backgroundColor || "#3b82f6",
                                                color: (element.props as any)?.color || "#ffffff"
                                              }}>
                                                {(element.props as any)?.text || "Button"}
                                              </button>
                                            </div>
                                          )}
                                          {element.type === "image" && (
                                            <div className="flex justify-center">
                                              <img 
                                                src={(element.props as any)?.src || "https://via.placeholder.com/400x200"} 
                                                alt={(element.props as any)?.alt || "Image"} 
                                                className="max-w-full max-h-48 object-contain rounded"
                                              />
                                            </div>
                                          )}
                                          {element.type === "pricing" && (
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                              {((element.props as any)?.plans || []).map((plan: any, index: number) => (
                                                <div key={index} className="p-4 border rounded-lg text-center">
                                                  <div className="font-semibold text-lg">{plan.name}</div>
                                                  <div className="text-2xl font-bold text-blue-600 my-2">{plan.price}</div>
                                                  <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                                                    {plan.features?.map((f: string, i: number) => (
                                                      <li key={i}>✓ {f}</li>
                                                    ))}
                                                  </ul>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          {element.type === "testimonial" && (
                                            <div className="text-center">
                                              <p className="text-lg italic text-gray-600 dark:text-gray-400 mb-4">"{(element.props as any)?.quote || "Great product!"}"</p>
                                              <p className="font-medium">{(element.props as any)?.author || "Anonymous"}</p>
                                              <p className="text-sm text-gray-500">{(element.props as any)?.role || ""}</p>
                                            </div>
                                          )}
                                          {element.type === "feature" && (
                                            <div className="text-center">
                                              <div className="text-4xl mb-2">{(element.props as any)?.icon || "⭐"}</div>
                                              <h3 className="text-lg font-semibold mb-2">{(element.props as any)?.title || "Feature"}</h3>
                                              <p className="text-gray-600 dark:text-gray-400">{(element.props as any)?.description || "Feature description"}</p>
                                            </div>
                                          )}
                                          {element.type === "form" && (
                                            <div className="max-w-md mx-auto space-y-3">
                                              <h3 className="text-lg font-semibold text-center">{(element.props as any)?.title || "Contact Us"}</h3>
                                              {((element.props as any)?.fields || ["name", "email"]).map((field: string, i: number) => (
                                                <div key={i} className="p-2 border rounded bg-gray-50 dark:bg-gray-700 text-gray-500">{field}</div>
                                              ))}
                                              <button className="w-full py-2 bg-blue-600 text-white rounded">{(element.props as any)?.buttonText || "Submit"}</button>
                                            </div>
                                          )}
                                          {element.type === "video" && (
                                            <div className="text-center">
                                              <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center">
                                                <span className="text-4xl">🎥</span>
                                              </div>
                                              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{(element.props as any)?.title || "Video"}</p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </SortableContext>
                              )}
                            </div>
                          </DroppableCanvas>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Element Edit Full-Screen Overlay */}
        {showEditDialog && editingElement && (
          <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900 overflow-y-auto" data-testid="dialog-edit-element">
            <div className="h-full w-full p-8">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-bold">
                  Edit {elementTypes.find(et => et.type === editingElement.type)?.label || "Element"}
                </h2>
                <Button 
                  onClick={() => setShowEditDialog(false)} 
                  variant="outline" 
                  size="sm"
                  data-testid="button-close-edit"
                >
                  <X className="w-4 h-4 mr-2" />
                  Close
                </Button>
              </div>
              
              <div className="w-full h-full">
                {editingElement.type === "text" && (
                  <ElementTextEditor
                    element={editingElement}
                    onSave={handleSaveElement}
                    onCancel={() => setShowEditDialog(false)}
                  />
                )}
                {editingElement.type === "button" && (
                  <ElementButtonEditor
                    element={editingElement}
                    onSave={handleSaveElement}
                    onCancel={() => setShowEditDialog(false)}
                  />
                )}
                {editingElement.type === "image" && (
                  <ElementImageEditor
                    element={editingElement}
                    onSave={handleSaveElement}
                    onCancel={() => setShowEditDialog(false)}
                  />
                )}
                {editingElement.type === "pricing" && (
                  <ElementPricingEditor
                    element={editingElement}
                    onSave={handleSaveElement}
                    onCancel={() => setShowEditDialog(false)}
                  />
                )}
                {!["text", "button", "image", "pricing"].includes(editingElement.type) && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Editing for {editingElement.type} elements is not yet available.</p>
                    <Button onClick={() => setShowEditDialog(false)} className="mt-4">Close</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Canvas Settings Dialog */}
        {showCanvasSettings && (
          <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900 overflow-y-auto" data-testid="dialog-canvas-settings">
            <div className="min-h-screen p-8">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-bold">Canvas Settings</h2>
                <Button 
                  onClick={() => setShowCanvasSettings(false)} 
                  variant="outline" 
                  size="sm"
                  data-testid="button-close-canvas-settings"
                >
                  <X className="w-4 h-4 mr-2" />
                  Close
                </Button>
              </div>
              
              <div className="max-w-2xl space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Canvas Display</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Button
                      variant={previewDevice === "desktop" ? "default" : "outline"}
                      onClick={() => setPreviewDevice("desktop")}
                      className="flex items-center gap-2"
                      data-testid="button-device-desktop"
                    >
                      <Monitor className="w-4 h-4" />
                      Desktop
                    </Button>
                    <Button
                      variant={previewDevice === "tablet" ? "default" : "outline"}
                      onClick={() => setPreviewDevice("tablet")}
                      className="flex items-center gap-2"
                      data-testid="button-device-tablet"
                    >
                      <Tablet className="w-4 h-4" />
                      Tablet
                    </Button>
                    <Button
                      variant={previewDevice === "mobile" ? "default" : "outline"}
                      onClick={() => setPreviewDevice("mobile")}
                      className="flex items-center gap-2"
                      data-testid="button-device-mobile"
                    >
                      <Smartphone className="w-4 h-4" />
                      Mobile
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Grid System</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    The grid system is currently visible and set to 50px major lines with 25px minor lines for precise element positioning.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Canvas Properties</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Canvas background and other properties can be configured here. Additional settings will be added in future updates.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview Dialog */}
        {showPreview && (
          <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900 overflow-y-auto" data-testid="dialog-preview">
            <div className="min-h-screen">
              {/* Preview Header */}
              <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 z-10">
                <div className="flex justify-between items-center p-4">
                  <div className="flex items-center gap-4">
                    <h2 className="text-xl font-bold">Live Preview</h2>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Click any element to edit it</span>
                    <Select value={previewDevice} onValueChange={(value: any) => setPreviewDevice(value)}>
                      <SelectTrigger className="w-32" data-testid="select-preview-device-dialog">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="desktop">
                          <div className="flex items-center gap-2">
                            <Monitor className="w-4 h-4" />
                            Desktop
                          </div>
                        </SelectItem>
                        <SelectItem value="tablet">
                          <div className="flex items-center gap-2">
                            <Tablet className="w-4 h-4" />
                            Tablet
                          </div>
                        </SelectItem>
                        <SelectItem value="mobile">
                          <div className="flex items-center gap-2">
                            <Smartphone className="w-4 h-4" />
                            Mobile
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={() => setShowPreview(false)} 
                    variant="outline" 
                    size="sm"
                    data-testid="button-close-preview"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Close Preview
                  </Button>
                </div>
              </div>

              {/* Preview Content */}
              <div className={`mx-auto transition-all ${
                previewDevice === "mobile" ? "max-w-sm" : 
                previewDevice === "tablet" ? "max-w-2xl" : 
                "max-w-6xl"
              }`}>
                <div className="bg-white dark:bg-gray-900 min-h-screen">
                  {elements.length === 0 ? (
                    <div className="flex items-center justify-center min-h-screen">
                      <div className="text-center">
                        <Globe className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                        <h3 className="text-lg font-medium text-gray-600 dark:text-gray-400 mb-2">No content to preview</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-500">Add elements to your landing page to see the preview</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-8 p-8">
                      {elements.map((element: LandingPageElement) => (
                        <div 
                          key={element.id} 
                          data-testid={`preview-element-${element.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setShowPreview(false);
                            handleEditElement(element);
                          }}
                          className="cursor-pointer hover:outline hover:outline-2 hover:outline-blue-500 hover:outline-offset-4 rounded-lg transition-all"
                          title="Click to edit"
                        >
                          {element.type === "text" && (
                            <div 
                              className="prose prose-lg max-w-none dark:prose-invert" 
                              style={{ 
                                textAlign: (element.props as any)?.textAlign || "left",
                                fontWeight: (element.props as any)?.fontWeight || "normal"
                              }}
                              dangerouslySetInnerHTML={{ 
                                __html: DOMPurify.sanitize((element.props as any)?.content || "<p>Text content</p>") 
                              }}
                            />
                          )}
                          {element.type === "button" && (
                            <div style={{ textAlign: (element.props as any)?.textAlign || "left" }}>
                              <span
                                className="inline-block px-6 py-3 rounded-lg font-medium text-white"
                                style={{
                                  backgroundColor: (element.props as any)?.backgroundColor || "#007bff",
                                  color: (element.props as any)?.color || "#ffffff"
                                }}
                              >
                                {(element.props as any)?.text || "Button"}
                              </span>
                            </div>
                          )}
                          {element.type === "image" && (
                            <div style={{ textAlign: (element.props as any)?.textAlign || "center" }}>
                              <img 
                                src={(element.props as any)?.src || "https://via.placeholder.com/800x400"} 
                                alt={(element.props as any)?.alt || "Image"} 
                                className="rounded-lg shadow-lg"
                                style={{
                                  width: (element.props as any)?.width || "100%",
                                  maxWidth: "100%",
                                  height: "auto"
                                }}
                              />
                            </div>
                          )}
                          {element.type === "pricing" && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              {((element.props as any)?.plans || []).map((plan: any, index: number) => (
                                <div 
                                  key={index} 
                                  className={`p-6 rounded-lg border-2 ${
                                    plan.featured 
                                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-lg scale-105" 
                                      : "border-gray-200 dark:border-gray-700"
                                  }`}
                                >
                                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-4">{plan.price}</div>
                                  <ul className="space-y-2 mb-6">
                                    {(plan.features || []).map((feature: string, i: number) => (
                                      <li key={i} className="flex items-start gap-2">
                                        <span className="text-green-500 mt-1">✓</span>
                                        <span>{feature}</span>
                                      </li>
                                    ))}
                                  </ul>
                                  <button className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                                    Get Started
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {!["text", "button", "image", "pricing"].includes(element.type) && (
                            <div className="p-6 bg-gray-100 dark:bg-gray-800 rounded-lg">
                              <p className="text-center text-gray-600 dark:text-gray-400">
                                {element.type} element preview
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Drag Overlay */}
        <DragOverlay>
          {activeId ? (
            <div className="p-3 bg-blue-100 dark:bg-blue-900 border-2 border-blue-400 dark:border-blue-500 rounded-lg opacity-80">
              <div className="flex items-center gap-2">
                <span className="text-lg">
                  {elementTypes.find(et => `element-${et.type}` === activeId)?.icon}
                </span>
                <span className="font-medium text-sm">
                  {elementTypes.find(et => `element-${et.type}` === activeId)?.label}
                </span>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}